import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  FiChevronDown,
  FiCopy,
  FiDownload,
  FiEdit2,
  FiEye,
  FiMoreVertical,
  FiPlus,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import {
  LuCheckCircle,
  LuClock3,
  LuIndianRupee,
  LuLandmark,
  LuSend,
  LuShield,
  LuSlidersHorizontal,
  LuAlertTriangle,
} from "react-icons/lu";
import PayoutDataTable from "./PayoutDataTable";
import PayoutEmptyState from "./PayoutEmptyState";
import PayoutDateRangeFilter from "./PayoutDateRangeFilter";
import PayoutDropdown from "./PayoutDropdown";
import PayoutFilterChips from "./PayoutFilterChips";
import PayoutFilterActions from "./PayoutFilterActions";
import PayoutKpiValue from "./PayoutKpiValue";
import PayoutSearchFilter from "./PayoutSearchFilter";
import PayoutCancelScheduledTransferModal from "./PayoutCancelScheduledTransferModal";
import PayoutTableCell from "./PayoutTableCell";
import { showPayoutNotification } from "./PayoutNotification";
import { formatPayoutDateRangeLabel, parsePayoutTableDate } from "./payoutDateUtils";
import { createPayoutIdempotencyKey, isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import {
  apiNumber,
  buildPaymentSearchQuery,
  transferReceiptFromApiRecord,
  transferRowFromApiRecord,
} from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";
import { errorMessage, pickString, readSessionJson } from "./api/payoutMutationHelpers";
import { usePayoutAccess } from "./payoutAccessControl";

const formatINR = (amount: number) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const toDateTimeLocalValue = (value?: string) => {
  const date = value ? new Date(value) : new Date(Date.now() + 24 * 60 * 60 * 1000);
  const usableDate = Number.isNaN(date.getTime())
    ? new Date(Date.now() + 24 * 60 * 60 * 1000)
    : date;
  const pad = (part: number) => String(part).padStart(2, "0");

  return `${usableDate.getFullYear()}-${pad(usableDate.getMonth() + 1)}-${pad(
    usableDate.getDate(),
  )}T${pad(usableDate.getHours())}:${pad(usableDate.getMinutes())}`;
};

const formatDateTimeLocal = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const transferStats = [
  {
    label: "Total Transfers",
    value: "2,548",
    trend: "+ 12.6% vs last week",
    icon: <LuSend />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
    trendClass: "text-[#16A34A]",
  },
  {
    label: "Successful Transfers",
    value: "2,234",
    trend: "+ 10.8% vs last week",
    icon: <LuCheckCircle />,
    tone: "bg-[#16A34A1A] text-[#16A34A]",
    trendClass: "text-[#16A34A]",
  },
  {
    label: "Pending Transfers",
    value: "186",
    trend: "- 3.4% vs last week",
    icon: <LuClock3 />,
    tone: "bg-[#F59E0B1A] text-[#F59E0B]",
    trendClass: "text-[#F97316]",
  },
  {
    label: "Failed Transfers",
    value: "128",
    trend: "- 8.2% vs last week",
    icon: <LuAlertTriangle />,
    tone: "bg-[#EF44441A] text-[#EF4444]",
    trendClass: "text-[#EF4444]",
  },
  {
    label: "Total Amount",
    value: 32445230,
    trend: "+ 15.3% vs last week",
    icon: <LuIndianRupee />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
    trendClass: "text-[#16A34A]",
  },
];

type TransferStat = (typeof transferStats)[number];

const displayText = (value?: string) => value?.trim() || "-";

const maskedLast4 = (accountNumber?: string) => {
  const last4 = accountNumber?.replace(/\D/g, "").slice(-4) || "";
  return last4 ? `****${last4}` : "-";
};

const accountDetailText = (bank?: string, accountNumber?: string) => {
  const bankName = bank?.trim() || "";
  const masked = maskedLast4(accountNumber);
  if (bankName && masked !== "-") return `${bankName} ${masked}`;
  return bankName || masked;
};

const transferStatsFromApiRecord = (
  summary: Record<string, unknown>,
  fallback: TransferStat[],
): TransferStat[] => {
  const totalTransfers = apiNumber(
    summary,
    [
      "total_count",
      "totalCount",
      "total_transfers",
      "totalTransfers",
      "transfers_count",
      "transfersCount",
      "count",
    ],
    0,
  );
  const successfulTransfers = apiNumber(
    summary,
    [
      "successful_count",
      "successfulCount",
      "successful_transfers",
      "successfulTransfers",
      "success_count",
      "successCount",
    ],
    0,
  );
  const pendingTransfers = apiNumber(
    summary,
    [
      "pending_count",
      "pendingCount",
      "pending_transfers",
      "pendingTransfers",
    ],
    0,
  );
  const failedTransfers = apiNumber(
    summary,
    [
      "failed_count",
      "failedCount",
      "failed_transfers",
      "failedTransfers",
    ],
    0,
  );
  const successfulAmount = apiNumber(
    summary,
    [
      "successful_amount_paise",
      "successfulAmountPaise",
      "successful_amount",
      "successfulAmount",
      "total_amount_paise",
      "totalAmountPaise",
      "amount_paise",
      "amountPaise",
      "total_amount",
      "totalAmount",
    ],
    0,
    Boolean(
      summary.successful_amount_paise ||
        summary.successfulAmountPaise ||
        summary.total_amount_paise ||
        summary.totalAmountPaise ||
        summary.amount_paise ||
        summary.amountPaise,
    ),
  );

  return fallback.map((stat) => {
    if (stat.label === "Total Transfers") {
      return { ...stat, value: totalTransfers.toLocaleString("en-IN"), trend: "" };
    }
    if (stat.label === "Successful Transfers") {
      return { ...stat, value: successfulTransfers.toLocaleString("en-IN"), trend: "" };
    }
    if (stat.label === "Pending Transfers") {
      return { ...stat, value: pendingTransfers.toLocaleString("en-IN"), trend: "" };
    }
    if (stat.label === "Failed Transfers") {
      return { ...stat, value: failedTransfers.toLocaleString("en-IN"), trend: "" };
    }
    return {
      ...stat,
      value: successfulAmount,
      trend: "",
    };
  });
};

const transferTabs = [
  {
    label: "Track Transfers",
    to: "/payouts/transfers",
  },
  {
    label: "Pending Approval",
    to: "/payouts/transfers/pending-approval",
  },
];

const parseCountValue = (value: TransferStat["value"]) => {
  if (typeof value === "number") return value;
  return Number(String(value || "0").replace(/[^\d.-]/g, "")) || 0;
};

const categoryClassName: Record<string, string> = {
  Vendor: "bg-[#EDE9FE] text-[#6D28D9]",
  Salary: "bg-[#DBEAFE] text-[#0284C7]",
  Scholarship: "bg-[#EDE9FE] text-[#6D28D9]",
  Refund: "bg-[#FCE7F3] text-[#DB2777]",
  Reimbursement: "bg-[#CCFBF1] text-[#0F766E]",
};

const statusClassName: Record<string, string> = {
  Processed: "bg-[#DCFCE7] text-[#16A34A]",
  Processing: "bg-[#DBEAFE] text-[#0284C7]",
  Pending: "bg-[#FEF3C7] text-[#F59E0B]",
  Scheduled: "bg-[#EDE9FE] text-[#6D28D9]",
  Failed: "bg-[#FEE2E2] text-[#EF4444]",
};

type TransferRow = {
  transferId: string;
  referenceId: string;
  utrNumber: string;
  beneficiary: string;
  detail: string;
  purpose: string;
  category: string;
  method: string;
  amount: number;
  status: string;
  createdAt: string;
  settlement: string;
  beneficiaryAccountNumber?: string;
  beneficiaryBank?: string;
  beneficiaryAccountType?: string;
  beneficiaryEmail?: string;
  beneficiaryMobile?: string;
  payoutAccount?: string;
  payoutAccountBank?: string;
  payoutAccountNumber?: string;
  payoutAccountFilter?: string;
  payoutAccountDetail?: string;
  scheduledFor?: string;
  scheduleType?: string;
  scheduledRemainingSeconds?: number;
  fundingIfsc?: string;
  beneficiaryIfsc?: string;
  beneficiaryVerificationStatus?: string;
  bankReference?: string;
  batchNo?: string;
  attemptStatus?: string;
  fees?: number;
  netAmount?: number;
  makerUserId?: string;
  failureReason?: string;
  timeline?: unknown[];
};

const emptyTransferRow: TransferRow = {
  transferId: "",
  referenceId: "",
  utrNumber: "",
  beneficiary: "",
  detail: "",
  purpose: "",
  category: "",
  method: "",
  amount: 0,
  status: "",
  createdAt: "",
  settlement: "",
  beneficiaryAccountNumber: "",
  payoutAccount: "",
  payoutAccountBank: "",
  payoutAccountNumber: "",
  payoutAccountFilter: "",
  scheduleType: "",
};

function DetailMetric({
  label,
  value,
  helper,
  icon,
  tone,
}: {
  label: string;
  value: string;
  helper?: string;
  icon: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="flex min-w-[190px] items-center gap-4 border-r border-[#E0E5F2] px-5 py-2 last:border-r-0">
      <span
        className={
          "payout-kpi-icon flex h-10 w-10 shrink-0 items-center justify-center text-lg " +
          tone
        }
      >
        {icon}
      </span>
      <span>
        <span className="block text-xs font-semibold text-[#505E8E]">
          {label}
        </span>
        <span className="mt-1 block text-sm font-semibold text-[#1B163B]">
          {value}
        </span>
        {helper && (
          <span className="mt-1 block text-xs font-medium text-[#505E8E]">
            {helper}
          </span>
        )}
      </span>
    </div>
  );
}

function DetailLine({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] py-3 text-sm md:grid-cols-[190px_minmax(0,1fr)]">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <div className="font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

function TransferDetailsDrawer({
  transfer: selectedTransfer,
  onClose,
}: {
  transfer: TransferRow | null;
  onClose: () => void;
}) {
  const [receiptTransfer, setReceiptTransfer] = useState<TransferRow | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState("");

  useEffect(() => {
    if (!selectedTransfer) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedTransfer]);

  useEffect(() => {
    setReceiptTransfer(selectedTransfer);
    setReceiptError("");
    if (!selectedTransfer?.transferId || !isPayoutApiMode()) return;

    let active = true;
    setReceiptLoading(true);
    payoutApi
      .paymentReceipt(selectedTransfer.transferId)
      .then((receipt) => {
        if (!active) return;
        setReceiptTransfer(
          transferReceiptFromApiRecord(receipt, selectedTransfer) as TransferRow,
        );
      })
      .catch((error) => {
        if (!active) return;
        setReceiptError(errorMessage(error, "Unable to load receipt details."));
      })
      .finally(() => {
        if (active) setReceiptLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedTransfer]);

  if (!selectedTransfer) return null;

  const transfer = receiptTransfer || selectedTransfer;

  const isSuccessful = transfer.status === "Processed";
  const visibleStatus = isSuccessful ? "Successful" : displayText(transfer.status);
  const completedOn = displayText(transfer.settlement);
  const fees = typeof transfer.fees === "number" ? transfer.fees : 0;
  const netAmount =
    typeof transfer.netAmount === "number" ? transfer.netAmount : transfer.amount + fees;
  const bulkDraft = readSessionJson<{
    fileName?: string;
    sizeKb?: number;
    rows?: Record<string, unknown>[];
  }>("payoutBulkTransferDraft", {});
  const bulkResult = readSessionJson<{
    batch_no?: string;
    batchNo?: string;
    items?: Record<string, unknown>[];
  }>("payoutBulkTransferResult", {});
  const bulkResultItems = Array.isArray(bulkResult.items) ? bulkResult.items : [];
  const bulkResultBatchNo = pickString(bulkResult, ["batch_no", "batchNo"]);
  const matchesBulkResult = bulkResultItems.some((item) => {
    const itemTransferId = pickString(item, ["payment_id", "paymentId", "transfer_id", "transferId", "id"]);
    const itemReferenceId = pickString(item, ["client_reference_id", "clientReferenceId", "reference_id", "referenceId"]);
    const itemBatchNo = pickString(item, ["batch_no", "batchNo"]);
    return (
      (!!itemTransferId && itemTransferId === transfer.transferId) ||
      (!!itemReferenceId && itemReferenceId === transfer.referenceId) ||
      (!!itemBatchNo && itemBatchNo === transfer.batchNo)
    );
  });
  const attachmentName = displayText(bulkDraft.fileName || "bulk-transfer-upload.xlsx");
  const showBulkAttachment =
    attachmentName !== "-" &&
    (matchesBulkResult ||
      (!!transfer.batchNo && (!bulkResultBatchNo || transfer.batchNo === bulkResultBatchNo)));
  const attachmentExt = attachmentName.includes(".")
    ? attachmentName.split(".").pop()?.toUpperCase() || "XLSX"
    : "XLSX";
  const attachmentSize =
    typeof bulkDraft.sizeKb === "number" && bulkDraft.sizeKb > 0
      ? `${bulkDraft.sizeKb} KB`
      : "-";

  const copyReference = () => navigator.clipboard?.writeText(transfer.transferId);

  const downloadReceipt = () => {
    const content = [
      "ClearN Transfer Receipt",
      `Transfer ID: ${transfer.transferId}`,
      `Reference ID: ${transfer.referenceId}`,
      `Beneficiary: ${transfer.beneficiary}`,
      `Amount: ${formatINR(transfer.amount)}`,
      `Fees: ${formatINR(fees)}`,
      `Net Amount: ${formatINR(netAmount)}`,
      `Method: ${transfer.method}`,
      `Bank Reference: ${displayText(transfer.bankReference || transfer.utrNumber)}`,
      `Status: ${visibleStatus}`,
      `Created At: ${transfer.createdAt}`,
    ].join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${transfer.transferId}-receipt.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex justify-end bg-[#1B163B]/20 backdrop-blur-[2px]">
      <button
        type="button"
        aria-label="Close transfer details"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <aside className="payout-detail-drawer relative z-10 h-full w-full max-w-[1180px] overflow-y-auto bg-white px-8 py-7 shadow-2xl">
        <div className="mb-7 flex items-start justify-between gap-5">
          <div>
            <h2 className="text-[22px] font-semibold text-[#1B163B]">
              Transfer Details
            </h2>
            <div className="mt-4 flex items-center gap-2">
              <span
                className={
                  "rounded-[6px] px-3 py-1 text-xs font-semibold " +
                  (statusClassName[transfer.status] || "bg-[#DCFCE7] text-[#16A34A]")
                }
              >
                {visibleStatus}
              </span>
              <span className="rounded-[6px] bg-[#A9B2CF1A] px-3 py-1 text-xs font-semibold text-[#505E8E]">
                {displayText(transfer.method)} Transfer
              </span>
            </div>
            <p className="mt-3 text-xs font-semibold text-[#505E8E]">
              Reference ID
            </p>
            <div className="mt-1 flex items-center gap-2 text-lg font-semibold text-[#1B163B]">
              {displayText(transfer.transferId)}
              <button type="button" onClick={copyReference}>
                <FiCopy className="text-sm text-[#505E8E]" />
              </button>
            </div>
            <p className="mt-2 text-sm font-medium text-[#505E8E]">
              Initiated on {displayText(transfer.createdAt)} {" • "} Completed on {completedOn}
            </p>
            {receiptLoading && (
              <p className="mt-2 text-xs font-semibold text-[#505E8E]">
                Loading latest receipt details...
              </p>
            )}
            {receiptError && (
              <p className="mt-2 text-xs font-semibold text-[#EF4444]">
                {receiptError}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={downloadReceipt}
              className="flex h-10 items-center gap-3 rounded-[6px] border border-[#BBD1FF] bg-white px-5 text-sm font-semibold text-[#2563EB]"
            >
              <FiDownload className="text-lg" />
              Download Receipt
            </button>
            <button className="flex h-10 w-10 items-center justify-center rounded-[6px] border border-[#E0E5F2] text-[#1B163B]">
              <FiMoreVertical />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-2xl text-[#1B163B]"
            >
              <FiX />
            </button>
          </div>
        </div>

        <section className="mb-5 overflow-x-auto rounded-[8px] border border-[#E0E5F2] bg-white py-5 new-shadow">
          <div className="flex min-w-[980px] items-center justify-between">
            <DetailMetric
              label="Transfer Amount"
              value={formatINR(transfer.amount)}
              icon={<LuShield />}
              tone="bg-[#6687FF1A] text-[#6687FF]"
            />
            <DetailMetric
              label="Fees & Charges"
              value={formatINR(fees)}
              icon={<LuIndianRupee />}
              tone="bg-[#6687FF1A] text-[#6687FF]"
            />
            <DetailMetric
              label="Net Amount"
              value={formatINR(netAmount)}
              icon={<LuSlidersHorizontal />}
              tone="bg-[#16A34A1A] text-[#16A34A]"
            />
            <DetailMetric
              label="Status"
              value={visibleStatus}
              icon={<LuCheckCircle />}
              tone="bg-[#16A34A1A] text-[#16A34A]"
            />
            <DetailMetric
              label="Completed On"
              value={completedOn}
              icon={<LuClock3 />}
              tone="bg-[#6687FF1A] text-[#6687FF]"
            />
          </div>
        </section>

        <div className="grid gap-5 xl:grid-cols-2">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-4 text-lg font-semibold text-[#1B163B]">
              Transfer Information
            </h3>
            <DetailLine
              label="Source Account"
              value={
                <span>
                  {displayText(transfer.payoutAccount)}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    {accountDetailText(transfer.payoutAccountBank, transfer.payoutAccountNumber)}
                    {transfer.fundingIfsc ? ` | IFSC: ${transfer.fundingIfsc}` : ""}
                  </span>
                </span>
              }
            />
            <DetailLine label="Transfer Type" value={displayText(transfer.method)} />
            <DetailLine label="Payment Mode" value={displayText(transfer.method)} />
            <DetailLine label="Transfer Date" value={displayText(transfer.createdAt)} />
            {transfer.scheduledFor && (
              <DetailLine label="Scheduled For" value={displayText(transfer.scheduledFor)} />
            )}
            <DetailLine label="Reference / Remark" value={displayText(transfer.purpose)} />
            <DetailLine label="Client Reference ID" value={displayText(transfer.referenceId)} />
            <DetailLine label="UTR / Bank Reference No." value={displayText(transfer.bankReference || transfer.utrNumber)} />
            <DetailLine label="Batch No." value={displayText(transfer.batchNo)} />
            <DetailLine label="Attempt Status" value={displayText(transfer.attemptStatus)} />
            <DetailLine label="Initiated By" value={displayText(transfer.makerUserId)} />
            <DetailLine label="Failure Reason" value={displayText(transfer.failureReason)} />
          </section>

          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-4 text-lg font-semibold text-[#1B163B]">
              Beneficiary Information
            </h3>
            <DetailLine label="Beneficiary Name" value={displayText(transfer.beneficiary)} />
            <DetailLine label="Account Number" value={displayText(transfer.beneficiaryAccountNumber)} />
            <DetailLine label="Bank Name" value={displayText(transfer.beneficiaryBank)} />
            <DetailLine label="IFSC Code" value={displayText(transfer.beneficiaryIfsc)} />
            <DetailLine label="Account Type" value={displayText(transfer.beneficiaryAccountType)} />
            <DetailLine label="Email" value={displayText(transfer.beneficiaryEmail)} />
            <DetailLine label="Mobile Number" value={displayText(transfer.beneficiaryMobile)} />
            <DetailLine label="Verification Status" value={displayText(transfer.beneficiaryVerificationStatus)} />
          </section>
        </div>

        {showBulkAttachment && (
        <div className="mt-5">
          <section className="max-w-[520px] rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-5 text-lg font-semibold text-[#1B163B]">
              Attachments (1)
            </h3>
            <div
              className="flex w-full items-center justify-between rounded-[6px] border border-[#E0E5F2] px-4 py-3 text-left"
            >
              <span className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-[6px] bg-[#FEE2E2] text-xs font-semibold text-[#EF4444]">
                  {attachmentExt}
                </span>
                <span className="text-sm font-semibold text-[#1B163B]">
                  {attachmentName}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    {attachmentSize}
                  </span>
                </span>
              </span>
              <span className="text-xs font-semibold text-[#505E8E]">
                Uploaded file
              </span>
            </div>
          </section>
        </div>
        )}

        <div className="mt-8 flex justify-end border-t border-[#E0E5F2] pt-6">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            Close
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
}

function SelectBox({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <PayoutDropdown
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      className={className}
    />
  );
}

function PayoutTransfers() {
  const { canAccess } = usePayoutAccess();
  const canCreateTransfers = canAccess("transfers.create");
  const defaultDateRange = useMemo(
    () => ({
      startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      endDate: new Date(),
      key: "selection",
    }),
    [],
  );
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [accountFilter, setAccountFilter] = useState("All Accounts");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [methodFilter, setMethodFilter] = useState("All Methods");
  const [pageSize, setPageSize] = useState("10 per page");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("Reference ID");
  const [dateRangeFilter, setDateRangeFilter] = useState(defaultDateRange);
  const [selectedTransfer, setSelectedTransfer] = useState<TransferRow | null>(null);
  const [scheduledTransferAction, setScheduledTransferAction] = useState<{
    type: "edit" | "delete";
    row: TransferRow;
  } | null>(null);
  const [cancellingScheduledTransfer, setCancellingScheduledTransfer] =
    useState(false);
  const [editScheduledAt, setEditScheduledAt] = useState("");
  const [scheduledTransferOverrides, setScheduledTransferOverrides] = useState<
    Record<string, { scheduledFor: string; status: string; settlement: string }>
  >({});
  const [deletedScheduledTransfers, setDeletedScheduledTransfers] = useState<string[]>([]);
  const [appliedFilters, setAppliedFilters] = useState({
    account: "All Accounts",
    category: "All Categories",
    status: "All Status",
    method: "All Methods",
    search: "",
    searchField: "Reference ID",
    dateRange: dateRangeFilter,
  });
  const rowsPerPage = Number(pageSize.split(" ")[0]) || 10;
  useEffect(() => {
    setAppliedFilters({
      account: accountFilter,
      category: categoryFilter,
      status: statusFilter,
      method: methodFilter,
      search: searchQuery,
      searchField,
      dateRange: dateRangeFilter,
    });
    setCurrentPage(1);
  }, [
    accountFilter,
    categoryFilter,
    dateRangeFilter,
    methodFilter,
    searchField,
    searchQuery,
    statusFilter,
  ]);
  const clearFilters = () => {
    setAccountFilter("All Accounts");
    setCategoryFilter("All Categories");
    setStatusFilter("All Status");
    setMethodFilter("All Methods");
    setSearchQuery("");
    setSearchField("Reference ID");
    setDateRangeFilter(defaultDateRange);
    setCurrentPage(1);
  };
  const apiTransferQuery = useMemo(
    () =>
      buildPaymentSearchQuery({
        status: appliedFilters.status,
        method: appliedFilters.method,
        search: appliedFilters.search,
        dateRange: appliedFilters.dateRange,
        limit: 100,
      }),
    [appliedFilters],
  );
  const emptyTransferStats = useMemo<TransferStat[]>(
    () =>
      transferStats.map((stat) =>
        stat.label === "Total Amount"
          ? ({ ...stat, value: 0, trend: "" } as TransferStat)
          : ({ ...stat, value: "0", trend: "" } as TransferStat),
      ),
    [],
  );
  const transferStatsResource = usePayoutResource<TransferStat[]>(
    emptyTransferStats,
    (signal) =>
      payoutApi
        .transfersSummary(apiTransferQuery, signal)
        .then((summary) => transferStatsFromApiRecord(summary, emptyTransferStats)),
    [apiTransferQuery, emptyTransferStats],
  );
  const displayedTransferStats =
    transferStatsResource.data.length > 0
      ? transferStatsResource.data
      : emptyTransferStats;
  const pendingApprovalsCountResource = usePayoutResource<number>(
    0,
    (signal) =>
      payoutApi.pendingApprovals({ limit: 1000 }, signal).then((rows) => rows.length),
    [],
  );
  const transfersResource = usePayoutResource<TransferRow[]>(
    [] as TransferRow[],
    (signal) =>
      payoutApi.paymentSearch(apiTransferQuery, signal).then((rows) =>
        rows.map((row) => transferRowFromApiRecord(row, emptyTransferRow) as TransferRow),
      ),
    [apiTransferQuery],
  );
  const baseTransferRows: TransferRow[] = transfersResource.data;
  const expandedTransferRows = useMemo(
    () => baseTransferRows,
    [baseTransferRows],
  );
  const transferRowsWithUpdates = useMemo(
    () =>
      expandedTransferRows
        .filter((row) => !deletedScheduledTransfers.includes(row.transferId))
        .map((row) => ({
          ...row,
          ...scheduledTransferOverrides[row.transferId],
        })),
    [deletedScheduledTransfers, expandedTransferRows, scheduledTransferOverrides],
  );
  const accountOptions = useMemo(
    () => [
      "All Accounts",
      ...Array.from(
        new Set(
          transferRowsWithUpdates
            .map((row) => row.payoutAccountFilter)
            .filter(Boolean) as string[],
        ),
      ),
    ],
    [transferRowsWithUpdates],
  );
  const categoryOptions = useMemo(
    () => [
      "All Categories",
      ...Array.from(new Set(transferRowsWithUpdates.map((row) => row.category).filter(Boolean))),
    ],
    [transferRowsWithUpdates],
  );
  const transferSearchOptions = [
    "Reference ID",
    "UTR Number",
    "Beneficiary Account Number",
    "Beneficiary Name",
  ];
  const getTransferSearchValue = (
    row: (typeof transferRowsWithUpdates)[number],
    field: string,
  ) => {
    switch (field) {
      case "UTR Number":
        return row.utrNumber;
      case "Beneficiary Account Number":
        return row.beneficiaryAccountNumber || row.detail || "";
      case "Beneficiary Name":
        return row.beneficiary;
      case "Reference ID":
      default:
        return row.referenceId;
    }
  };
  const filteredTransferRows = useMemo(() => {
    const query = appliedFilters.search.trim().toLowerCase();
    return transferRowsWithUpdates.filter((row) => {
      const matchesAccount =
        appliedFilters.account === "All Accounts" ||
        row.payoutAccountFilter === appliedFilters.account;
      const matchesCategory =
        appliedFilters.category === "All Categories" ||
        row.category === appliedFilters.category;
      const matchesStatus =
        appliedFilters.status === "All Status" ||
        row.status === appliedFilters.status;
      const matchesMethod =
        appliedFilters.method === "All Methods" ||
        row.method === appliedFilters.method;
      const rowDate = parsePayoutTableDate(row.createdAt);
      const matchesDate =
        rowDate !== null &&
        rowDate >= appliedFilters.dateRange.startDate &&
        rowDate <= appliedFilters.dateRange.endDate;
      const matchesSearch =
        !query ||
        getTransferSearchValue(row, appliedFilters.searchField)
          .toLowerCase()
          .includes(query);

      return matchesAccount && matchesCategory && matchesStatus && matchesMethod && matchesDate && matchesSearch;
    });
  }, [appliedFilters, transferRowsWithUpdates]);
  const pageCount = Math.max(1, Math.ceil(filteredTransferRows.length / rowsPerPage));
  const visibleTransferRows = filteredTransferRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const resultStart = filteredTransferRows.length
    ? (currentPage - 1) * rowsPerPage + 1
    : 0;
  const resultEnd = Math.min(currentPage * rowsPerPage, filteredTransferRows.length);
  const totalTransfersMetric = displayedTransferStats.find(
    (stat) => stat.label === "Total Transfers",
  );
  const transferTabCount =
    parseCountValue(totalTransfersMetric?.value ?? 0) || transferRowsWithUpdates.length;
  const pendingApprovalsTabCount = pendingApprovalsCountResource.data;

  const exportTransfers = () => {
    const headers = [
      "Transfer ID",
      "Reference ID",
      "UTR Number",
      "Beneficiary",
      "Payout Account",
      "Category",
      "Method",
      "Amount",
      "Status",
      "Created At",
      "Scheduled For",
      "Expected Settlement",
    ];
    const csvRows = filteredTransferRows.map((row) =>
      [
        displayText(row.transferId),
        displayText(row.referenceId),
        displayText(row.utrNumber),
        displayText(row.beneficiary),
        accountDetailText(row.payoutAccountBank, row.payoutAccountNumber),
        displayText(row.category),
        displayText(row.method),
        row.amount,
        displayText(row.status),
        displayText(row.createdAt),
        displayText(row.scheduledFor),
        displayText(row.settlement),
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "payout-transfers.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const openScheduledEdit = (row: TransferRow) => {
    setEditScheduledAt(toDateTimeLocalValue(row.scheduledFor));
    setScheduledTransferAction({ type: "edit", row });
  };

  const saveScheduledTransfer = () => {
    if (!scheduledTransferAction || !editScheduledAt) return;

    const scheduledFor = formatDateTimeLocal(editScheduledAt);
    setScheduledTransferOverrides((current) => ({
      ...current,
      [scheduledTransferAction.row.transferId]: {
        scheduledFor,
        status: "Scheduled",
        settlement: "Scheduled",
      },
    }));
    setScheduledTransferAction(null);
  };

  const deleteScheduledTransfer = async () => {
    if (!scheduledTransferAction || cancellingScheduledTransfer) return;
    const paymentId = scheduledTransferAction.row.transferId;
    setCancellingScheduledTransfer(true);

    if (isPayoutApiMode()) {
      try {
        await payoutApi.cancelPayment(
          paymentId,
          { reason: "Cancelled during cooling-off window" },
          createPayoutIdempotencyKey("cancel-scheduled", paymentId),
        );
        transfersResource.refresh();
        setScheduledTransferAction(null);
      } catch (error) {
        showPayoutNotification({
          tone: "error",
          message: errorMessage(error, "Unable to cancel this scheduled transfer."),
        });
      } finally {
        setCancellingScheduledTransfer(false);
      }
      return;
    }

    setDeletedScheduledTransfers((current) => [
      ...current,
      scheduledTransferAction.row.transferId,
    ]);
    setScheduledTransferAction(null);
    setCancellingScheduledTransfer(false);
  };

  const transferTableRows = visibleTransferRows.map((row) => [
    <PayoutTableCell
      value={displayText(row.transferId)}
      onClick={() => setSelectedTransfer(row)}
      valueClassName="font-semibold text-[#6687FF]"
    />,
    <PayoutTableCell
      value={displayText(row.referenceId)}
      onClick={() => setSelectedTransfer(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(row.utrNumber)}
      onClick={() => setSelectedTransfer(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <span>
      <PayoutTableCell
        value={displayText(row.beneficiary)}
        copyValue={[displayText(row.beneficiary), displayText(row.detail)].join(" ")}
        onClick={() => setSelectedTransfer(row)}
        valueClassName="font-medium text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate text-[#505E8E]" title={displayText(row.detail)}>
        {displayText(row.detail)}
      </span>
    </span>,
    <span>
      <PayoutTableCell
        value={displayText(row.payoutAccount)}
        copyValue={[
          displayText(row.payoutAccount),
          accountDetailText(row.payoutAccountBank, row.payoutAccountNumber),
        ].join(" ")}
        onClick={() => setSelectedTransfer(row)}
        valueClassName="font-medium text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate text-[#505E8E]" title={accountDetailText(row.payoutAccountBank, row.payoutAccountNumber)}>
        {accountDetailText(row.payoutAccountBank, row.payoutAccountNumber)}
      </span>
    </span>,
    <button
      type="button"
      onClick={() => setSelectedTransfer(row)}
      className="block w-full text-left"
    >
      <span
        className={
          "rounded-[5px] px-2 py-1 text-xs font-semibold " +
          (categoryClassName[row.category] || "bg-[#EEF2F7] text-[#505E8E]")
        }
      >
        {displayText(row.category)}
      </span>
    </button>,
    <button
      type="button"
      onClick={() => setSelectedTransfer(row)}
      className="inline-flex w-full items-center gap-2 text-left font-medium text-[#1B163B]"
    >
      <LuLandmark className="text-[#505E8E]" />
      {displayText(row.method)}
    </button>,
    <PayoutTableCell
      value={formatINR(row.amount)}
      onClick={() => setSelectedTransfer(row)}
      valueClassName="font-semibold text-[#1B163B]"
    />,
    <button
      type="button"
      onClick={() => setSelectedTransfer(row)}
      className="block w-full text-left"
    >
      <span
        className={
          "rounded-[5px] px-2 py-1 text-xs font-semibold " +
          (statusClassName[row.status] || "bg-[#EEF2F7] text-[#505E8E]")
        }
      >
        {displayText(row.status)}
      </span>
    </button>,
    <PayoutTableCell
      value={displayText(row.createdAt)}
      onClick={() => setSelectedTransfer(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(row.scheduledFor)}
      onClick={() => setSelectedTransfer(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(row.settlement)}
      onClick={() => setSelectedTransfer(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <span className="payout-action-cell">
      <button
        type="button"
        onClick={() => setSelectedTransfer(row)}
        className="payout-action-icon"
        aria-label={`View details for ${row.transferId}`}
        title="View details"
      >
        <FiEye className="text-base" />
      </button>
      {row.status === "Scheduled" && canCreateTransfers ? (
        <>
          {!isPayoutApiMode() ? (
            <button
              type="button"
              onClick={() => openScheduledEdit(row)}
              className="payout-action-icon"
              aria-label={`Edit scheduled transfer ${row.transferId}`}
              title="Edit scheduled transfer"
            >
              <FiEdit2 className="text-base" />
              <span className="sr-only">Edit scheduled transfer</span>
            </button>
          ) : (
            <span className="payout-action-placeholder" />
          )}
          <button
            type="button"
            onClick={() => setScheduledTransferAction({ type: "delete", row })}
            className="payout-action-icon payout-action-icon--danger"
            aria-label={`Cancel scheduled transfer ${row.transferId}`}
            title={isPayoutApiMode() ? "Cancel scheduled transfer" : "Delete scheduled transfer"}
          >
            <FiTrash2 className="text-base" />
            <span className="sr-only">
              {isPayoutApiMode()
                ? "Cancel scheduled transfer"
                : "Delete scheduled transfer"}
            </span>
          </button>
        </>
      ) : (
        <>
          <span className="payout-action-placeholder" />
          <span className="payout-action-placeholder" />
        </>
      )}
    </span>,
  ]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Transfers
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Track and manage all your payout transfers
          </p>
        </div>
        {canCreateTransfers && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setActionMenuOpen((current) => !current)}
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white shadow-sm hover:bg-edviron_black/90"
            >
              <FiPlus className="text-lg" />
              Initiate Transfer
              <FiChevronDown
                className={
                  "transition-transform " + (actionMenuOpen ? "rotate-180" : "")
                }
              />
            </button>
            {actionMenuOpen && (
              <div className="absolute right-0 z-30 mt-2 w-[220px] overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
                <Link
                  to="/payouts/transfers/initiate"
                  onClick={() => setActionMenuOpen(false)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-[#1B163B] hover:bg-[#A9B2CF1A]"
                >
                  <FiPlus className="text-[#6687FF]" />
                  Single Transfer
                </Link>
                <Link
                  to="/payouts/transfers/bulk-upload"
                  onClick={() => setActionMenuOpen(false)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-[#1B163B] hover:bg-[#A9B2CF1A]"
                >
                  <FiUploadCloud className="text-[#6687FF]" />
                  Bulk Upload
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-5">
        <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
          {displayedTransferStats.map((stat) => (
            <div
              key={stat.label}
              className="flex min-h-[112px] items-center gap-4 rounded-[8px] border border-[#E0E5F2] bg-white px-4 py-5 new-shadow"
            >
              <div
                className={
                  "payout-kpi-icon flex h-10 w-10 shrink-0 items-center justify-center text-lg " +
                  stat.tone
                }
              >
                {stat.icon}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#505E8E]">
                  {stat.label}
                </p>
                <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
                  {stat.label === "Total Amount" ? "\u20B9" : ""}
                  <PayoutKpiValue value={stat.value} />
                </p>
                {stat.trend && (
                  <p className={"mt-3 text-xs font-semibold " + stat.trendClass}>
                    {stat.trend}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="payout-filter-card relative z-20 mb-5 rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E0E5F2] px-5">
          <div className="flex items-center">
            {transferTabs.map((tab) => (
              <Link
                key={tab.label}
                to={tab.to}
                className={
                  "relative flex h-14 items-center px-7 text-sm font-semibold " +
                  (tab.label === "Track Transfers"
                    ? "text-[#6687FF]"
                    : "text-[#1B163B]")
                }
              >
                {tab.label}
                <span
                  className={
                    "ml-2 rounded-full px-2 py-0.5 text-xs font-semibold " +
                    (tab.label === "Track Transfers"
                      ? "bg-[#6687FF1A] text-[#6687FF]"
                      : "bg-[#FED7AA] text-[#F97316]")
                  }
                >
                  {(tab.label === "Track Transfers"
                    ? transferTabCount
                    : pendingApprovalsTabCount
                  ).toLocaleString("en-IN")}
                </span>
                {tab.label === "Track Transfers" && (
                  <span className="absolute bottom-0 left-0 h-[2px] w-full bg-[#6687FF]" />
                )}
              </Link>
            ))}
          </div>
        </div>

        <div className="payout-filter-grid payout-filter-grid--with-search px-5 py-5">
          <PayoutSearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            filterBy={searchField}
            onFilterByChange={setSearchField}
            filterOptions={transferSearchOptions}
            placeholder={`Search by ${searchField}`}
            className="w-full lg:col-span-2"
          />
          <PayoutDateRangeFilter
            label="Date Range"
            onChange={setDateRangeFilter}
            className="w-full min-w-0"
          />
          <SelectBox
            label="Account"
            value={accountFilter}
            options={accountOptions}
            onChange={setAccountFilter}
            className="w-full min-w-0"
          />
          <SelectBox
            label="Category"
            value={categoryFilter}
            options={categoryOptions}
            onChange={setCategoryFilter}
            className="w-full min-w-0"
          />
          <SelectBox
            label="Payout Method"
            value={methodFilter}
            options={["All Methods", "NEFT", "RTGS", "IMPS"]}
            onChange={setMethodFilter}
            className="w-full min-w-0"
          />
          <SelectBox
            label="Status"
            value={statusFilter}
            options={[
              "All Status",
              "Processed",
              "Processing",
              "Pending",
              "Scheduled",
              "Failed",
            ]}
            onChange={setStatusFilter}
            className="w-full min-w-0"
          />
          <PayoutFilterActions
            onClear={clearFilters}
            onExport={exportTransfers}
            className="w-full"
          />
          <PayoutFilterChips
            className="col-span-full"
            onClearAll={clearFilters}
            chips={[
              {
                label: "Date Range",
                value: formatPayoutDateRangeLabel(appliedFilters.dateRange),
                hidden:
                  appliedFilters.dateRange.startDate.getTime() ===
                    defaultDateRange.startDate.getTime() &&
                  appliedFilters.dateRange.endDate.getTime() ===
                    defaultDateRange.endDate.getTime(),
                onRemove: () => setDateRangeFilter(defaultDateRange),
              },
              {
                label: "Account",
                value: appliedFilters.account,
                onRemove: () => setAccountFilter("All Accounts"),
              },
              {
                label: "Category",
                value: appliedFilters.category,
                onRemove: () => setCategoryFilter("All Categories"),
              },
              {
                label: "Status",
                value: appliedFilters.status,
                onRemove: () => setStatusFilter("All Status"),
              },
              {
                label: "Payout Method",
                value: appliedFilters.method,
                onRemove: () => setMethodFilter("All Methods"),
              },
              {
                label: "Search",
                value: appliedFilters.search,
                onRemove: () => setSearchQuery(""),
              },
            ]}
          />
        </div>
      </section>

      <PayoutDataTable
        minWidth="min-w-[1480px]"
        columns={[
          "Transfer ID",
          "Reference ID",
          "UTR Number",
          "Beneficiary",
          "Payout Account",
          "Category",
          "Method",
          "Amount",
          "Status",
          "Created At",
          "Scheduled For",
          "Expected Settlement",
          "Actions",
        ]}
        rows={transferTableRows}
        emptyState={
          <PayoutEmptyState
            icon={<LuSend />}
            title="No payouts found"
            description="Transfers you initiate, schedule or process will appear here with status, beneficiary and settlement details."
            action={
              canCreateTransfers ? (
                <Link
                  to="/payouts/transfers/initiate"
                  className="flex h-10 items-center gap-2 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                >
                  <FiPlus /> Initiate Transfer
                </Link>
              ) : null
            }
          />
        }
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 border-t border-[#E0E5F2] px-5 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing {resultStart} to {resultEnd} of {filteredTransferRows.length} results
            </span>
            <div className="ml-auto flex min-w-max items-center gap-4">
              <SelectBox
                label=""
                value={pageSize}
                options={["10 per page", "25 per page", "50 per page"]}
                onChange={setPageSize}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowLeft className="text-xs" />
                </button>
                {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                  <button
                    type="button"
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === page
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {page}
                  </button>
                ))}
                {pageCount > 3 && (
                  <>
                    <span className="px-2 text-[#505E8E]">...</span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(pageCount)}
                      className={
                        "h-9 w-9 rounded-md " +
                        (currentPage === pageCount
                          ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                          : "border border-[#E0E5F2] text-[#1B163B]")
                      }
                    >
                      {pageCount}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage === pageCount}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        }
      />

      <TransferDetailsDrawer
        transfer={selectedTransfer}
        onClose={() => setSelectedTransfer(null)}
      />

      {scheduledTransferAction?.type === "edit" && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#1B163B]/35 px-4">
          <div className="w-full max-w-[520px] rounded-[10px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-[#1B163B]">
                  {scheduledTransferAction.type === "edit"
                    ? "Edit Scheduled Transfer"
                    : isPayoutApiMode()
                      ? "Cancel Scheduled Transfer"
                      : "Delete Scheduled Transfer"}
                </h2>
                <p className="mt-1 text-sm font-medium text-[#505E8E]">
                  {scheduledTransferAction.row.transferId} {" | "}
                  {scheduledTransferAction.row.beneficiary}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setScheduledTransferAction(null)}
                className="text-xl text-[#1B163B]"
              >
                <FiX />
              </button>
            </div>

            <div className="space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-[#505E8E]">
                  Scheduled date and time
                </span>
                <input
                  type="datetime-local"
                  value={editScheduledAt}
                  onChange={(event) => setEditScheduledAt(event.target.value)}
                  className="h-11 w-full rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-semibold text-[#1B163B] outline-none focus:border-[#6687FF]"
                />
              </label>
              <div className="rounded-[8px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#505E8E]">
                This updates only the scheduled execution time in the current
                frontend view.
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setScheduledTransferAction(null)}
                  className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveScheduledTransfer}
                  className="h-10 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {scheduledTransferAction?.type === "delete" && (
        <PayoutCancelScheduledTransferModal
          transactionId={scheduledTransferAction.row.transferId}
          beneficiary={scheduledTransferAction.row.beneficiary}
          amount={formatINR(scheduledTransferAction.row.amount)}
          scheduledFor={displayText(scheduledTransferAction.row.scheduledFor)}
          confirming={cancellingScheduledTransfer}
          onClose={() => setScheduledTransferAction(null)}
          onConfirm={() => void deleteScheduledTransfer()}
        />
      )}
    </div>
  );
}

export default PayoutTransfers;
